import { useState, useEffect } from 'react';
import { getPhotos, savePhotoMetadata, deletePhotoMetadata, updateJobPhotoCount } from '../firebase/db';
import { uploadPhoto, deletePhoto } from '../firebase/storage';
import { useAuth } from '../contexts/AuthContext';
import { useDemo } from '../contexts/DemoContext';
import { generateDemoPhotos } from '../demo/demoData';

import { compressImage } from '../utils/imageCompression';

export const usePhotos = (jobId) => {
  const { user, profile } = useAuth();
  const { demoMode } = useDemo();
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchPhotos = async () => {
    setLoading(true);
    if (demoMode) {
      setPhotos(generateDemoPhotos(jobId));
      setLoading(false);
      return;
    }
    const data = await getPhotos(jobId);
    setPhotos(data);
    setLoading(false);
  };

  const addPhoto = async (file) => {
    if (demoMode) {
      const photo = {
        id: 'demo_photo_' + Date.now(),
        jobId,
        url: URL.createObjectURL(file),
        name: file.name,
        size: file.size,
        uploadedBy: user?.uid,
        uploaderName: profile?.displayName || 'Demo Worker',
        uploadedAt: new Date().toISOString(),
      };
      setPhotos((prev) => [photo, ...prev]);
      return { photo, error: null };
    }

    setUploading(true);
    setProgress(0);

    // ── Compress image before upload ──
    let fileToUpload = file;
    if (file.type.startsWith('image/') && file.type !== 'image/gif') {
      try {
        const compressed = await compressImage(file, {
          maxWidth: 1920,
          maxHeight: 1920,
          quality: 0.8,
        });
        fileToUpload = compressed;
      } catch (err) {
        console.warn('[usePhotos] Compression failed, uploading original:', err.message);
        fileToUpload = file;
      }
    }

    const path = `jobs/${jobId}/${Date.now()}_${fileToUpload.name}`;
    const { url, fullPath, error } = await uploadPhoto(fileToUpload, path);
    if (error) {
      setUploading(false);
      setProgress(0);
      return { photo: null, error };
    }

    setProgress(100);
    const { id, error: metaError } = await savePhotoMetadata({
      jobId,
      url,
      storagePath: fullPath,
      name: fileToUpload.name,
      size: fileToUpload.size,
      uploadedBy: user?.uid,
      uploaderName: profile?.displayName || '',
    });

    setUploading(false);
    setProgress(0);
    if (metaError) return { photo: null, error: metaError };

    const photo = { id, jobId, url, name: fileToUpload.name, size: fileToUpload.size, uploadedBy: user?.uid, uploaderName: profile?.displayName || '', uploadedAt: new Date().toISOString() };
    setPhotos((prev) => [photo, ...prev]);
    // Update job photo count
    if (jobId) updateJobPhotoCount(jobId);
    return { photo, error: null };
  };

  const removePhoto = async (photo) => {
    if (demoMode) {
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
      return { error: null };
    }
    if (photo.storagePath) {
      await deletePhoto(photo.storagePath);
    }
    const { error } = await deletePhotoMetadata(photo.id);
    if (!error) {
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
      // Update job photo count
      if (jobId) updateJobPhotoCount(jobId);
    }
    return { error };
  };

  useEffect(() => {
    if (jobId && (user || demoMode)) fetchPhotos();
  }, [jobId, user, demoMode]);

  return { photos, loading, uploading, progress, fetchPhotos, addPhoto, removePhoto };
};
